import { useEffect, useRef, useState } from 'react';
import Button from '../atoms/Button';

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  onFailed?: (txHash?: string) => void;
}

type ChainID = 'TRC-20' | 'ERC-20';

const CHAIN_COPY: Record<ChainID, string> = {
  'TRC-20': '~1-3 min',
  'ERC-20': '~3-15 min, gas high',
};

export function PaymentModal({ open, onClose, onFailed }: PaymentModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [chain, setChain] = useState<ChainID>('TRC-20');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setChain('TRC-20');
      setCopied(false);
      return;
    }
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const address = chain === 'TRC-20'
    ? 'TQ9xPmYaB3kVZjCfyd7m2p'
    : '0x5Fyd8B8956A4A8B7C6B5B8E';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-title"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-ink/40 px-6 py-8 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-lg border border-hairline bg-paper p-8 shadow-2xl animate-modalIn">
        <h2 id="payment-title" className="font-serif text-2xl font-medium text-ink">
          支付 5 USDT 启动议会
        </h2>
        <p className="mt-1 text-sm text-ink-2">链上确认后自动启动 6-phase 议会。</p>

        <section className="mt-6 rounded border border-hairline bg-canvas p-4">
          <div className="mb-2 font-mono text-xs uppercase tracking-hud text-ink-2">Chain</div>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(CHAIN_COPY) as ChainID[]).map((id) => (
              <button
                key={id}
                type="button"
                className={`rounded border px-3 py-2 text-sm transition ${
                  chain === id
                    ? 'border-jade bg-jade text-ink-inv'
                    : 'border-hairline bg-paper text-ink-2 hover:border-jade'
                }`}
                onClick={() => setChain(id)}
              >
                <span className="block font-medium">{id}</span>
                <span className="block text-xs opacity-80">{CHAIN_COPY[id]}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="mt-4 grid gap-4 rounded border border-hairline bg-canvas p-4 sm:grid-cols-[8rem_1fr]">
          <div className="grid aspect-square place-items-center rounded bg-ink p-3 text-center font-mono text-xs uppercase leading-relaxed text-ink-inv">
            QR<br />CODE<br />5 USDT
          </div>
          <div className="min-w-0">
            <div className="font-mono text-xs uppercase tracking-hud text-ink-2">Send to</div>
            <div className="mt-2 break-all rounded border border-hairline bg-paper px-3 py-2 font-mono text-xs leading-relaxed text-ink">
              {address}
            </div>
            <button
              type="button"
              className="mt-2 text-sm text-jade-700 hover:text-jade"
              onClick={() => setCopied(true)}
            >
              {copied ? '已复制' : '复制地址'}
            </button>
          </div>
        </section>

        <section className="mt-4 flex items-center justify-between rounded border border-gold-200 bg-gold-100 p-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-hud text-gold-700">Amount</div>
            <div className="text-2xl font-semibold text-ink">5.00 USDT</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-xs uppercase tracking-hud text-gold-700">Expires</div>
            <div className="font-mono text-sm font-semibold text-ink">23h 47m</div>
          </div>
        </section>

        <div className="mt-4 rounded border border-azure-100 bg-azure-50 p-3 text-xs leading-relaxed text-azure-700">
          等待区块链确认 · 0/2 confirmations · 转账后此处会自动更新。
        </div>

        <div className="mt-6 flex gap-3">
          <Button ref={cancelRef} variant="secondary" block onClick={onClose}>
            回到议题输入
          </Button>
          <Button
            variant="vermilion"
            block
            onClick={() => onFailed?.('0xfyd...timeout')}
          >
            模拟失败
          </Button>
        </div>
      </div>
    </div>
  );
}
